import { Popover, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { percentRender } from 'shared/components/Render/PercentRender';
import { PriceRender } from 'shared/components/Render/PriceRender';
import { twoDigitRounded } from 'shared/components/Render/TwoDigitsRounder';
import { FinalAmount, Rem } from 'shared/components/Styles';
import { TroncatedText } from 'shared/components/TroncatedText/TroncatedText';
import { ID } from 'shared/utils/types';
import { OrderLink, ThingsContainer, ThingsHelperTable } from '../Styles';
import { ordersActions, ordersSelectors } from '../stores';

/**
 * Component helps to display Order- Items & Events from the OrderTable
 */
interface OrderTableTooltipProps {
  orderId: ID;
  orderRef: string;
}

export const OrderTableTooltip: FC<OrderTableTooltipProps> = ({
  orderId,
  orderRef,
}) => {
  const { t } = useTranslation();

  const { data: orderItems, loading: loadingItems } = useSelector(ordersSelectors.getOrderItems)
  const { data: orderEvents, loading: loadingEvents } = useSelector(ordersSelectors.getOrderEvents)

  const dispatch = useDispatch();

  const title = t("Orders.Others.Details", { orderId: orderRef });

  const content = () => {
    const itemsToDisplay = orderItems?.map(
      item => {
        return {
          ...item,
          creationDate: dayjs(item.creationDateTime).format('L LT'),
          lastUpdateDate: dayjs(item.lastUpdateDateTime).format('L LT'),
        };
      }
    );

    const eventsToDisplay = orderEvents ? orderEvents.map(event => ({
      ...event,
      eventDate: dayjs(event.eventDateTime).format('L LT')
    })) : undefined;

    return (
      <Space direction="vertical">
        <>
          {!loadingItems && itemsToDisplay?.length ? (
            <ThingsContainer>
              <h5>{t("Orders.Others.Products")}</h5>
              <ThingsHelperTable>
                <tr>
                  <th>{t("Orders.Columns.reference")}</th>
                  <th>{t("Orders.Columns.designation")}</th>
                  <th>{t("Orders.Columns.deliveredQuantity")}</th>
                  <th>{t("Orders.Columns.wantedQuantity")}</th>
                  <th>{t("Orders.Columns.unitPrice")}</th>
                  <th>{t("Orders.Columns.discountRate")}</th>
                  <th>{t("Orders.Columns.amount")}</th>
                </tr>
                {itemsToDisplay.map(item => {
                  return (
                    <tr>
                      <td>{item.reference}</td>
                      <td>{item.designation || '_'}</td>
                      <td>{item.deliveredQuantity}</td>
                      <td>{item.wantedQuantity}</td>
                      <td>
                        <PriceRender value={twoDigitRounded(item.price / item.wantedQuantity)} />
                      </td>
                      <td>
                        <Rem>{percentRender(item.discountRate)}</Rem>
                      </td>
                      <td>
                        <FinalAmount className="finalAmount">
                          <PriceRender value={twoDigitRounded((item.price * (1 - item.discountRate / 100)) * item.deliveredQuantity)} />
                        </FinalAmount>
                      </td>
                    </tr>
                  );
                })}
              </ThingsHelperTable>
            </ThingsContainer>
          ) : <Spin />}
          {!loadingEvents && eventsToDisplay?.length ? (
            <ThingsContainer>
              <h5>{t("Orders.Others.Events")}</h5>
              <ThingsHelperTable>
                <tr>
                  <th>{t("Orders.Columns.eventDate")}</th>
                  <th>{t("Orders.Columns.eventType")}</th>
                </tr>
                {eventsToDisplay.map(event => {
                  const { eventDate, eventType, eventComment } = event
                  if (eventType) {
                    return (
                      <tr>
                        <td>{eventDate}</td>
                        <td>{eventType}</td>
                        <td><TroncatedText lengthToBreakOn={50} value={eventComment} /></td>
                      </tr>
                    );
                  }
                  return <></>
                })}
              </ThingsHelperTable>
            </ThingsContainer>
          ) : <Spin />}
        </>
      </Space>
    );
  };

  const handleVisibleChange = (visible: any) => {
    if (visible && orderId) {
      dispatch(ordersActions.getOrderItems(orderId));
      dispatch(ordersActions.getOrderEvents(orderId));
    }
  };

  return (
    <Popover
      placement="right"
      content={content}
      title={title}
      onOpenChange={handleVisibleChange}
    >
      <OrderLink>{orderRef}</OrderLink>
    </Popover>
  );
};
